import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  StatusPill,
  Skeleton,
  Input,
  Button,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import {
  ORDER_STATUS_LABEL,
  ORDER_STATUS_TONE,
  orderMoney,
  shortDate,
  type OrderStatus,
} from "./order-format.ts";
import { shortDay } from "../date-window.ts";
import { PrebookPill } from "../prebook-pill.tsx";

/** Spec 16 R1 — the kind facet, as tabs in the same row as the statuses. */
const KIND_TABS = ["at_once", "prebook"] as const;
type OrderKind = (typeof KIND_TABS)[number];

/**
 * One row, one filter.
 *
 * Status and kind are orthogonal questions, and the server still accepts both at
 * once — but two controls side by side meant **two "All" chips** on the same
 * line, which reads as a bug however correct each half is. A single tab row has
 * exactly one "All", and the counts stay honest because both facets sum to it:
 * `draft+confirmed+approved+fulfilled+rejected == all == atOnce+prebook`.
 *
 * The cost is that "confirmed pre-books" is no longer askable from the UI. That
 * was a deliberate call; `storeOrders.list` still takes `status` and `kind`
 * together, so restoring it is a UI-only change if it turns out to matter.
 */
type Tab = "all" | OrderStatus | OrderKind;

const isKindTab = (t: Tab): t is OrderKind => (KIND_TABS as readonly string[]).includes(t);

const TABS: { key: Tab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "confirmed", label: "Confirmed" },
  { key: "approved", label: "Approved" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "rejected", label: "Rejected" },
  { key: "at_once", label: "At-once" },
  { key: "prebook", label: "Pre-book" },
];

const EMPTY_COUNTS = {
  all: 0,
  draft: 0,
  confirmed: 0,
  approved: 0,
  fulfilled: 0,
  rejected: 0,
  atOnce: 0,
  prebook: 0,
  open: 0,
  pastDispatch: 0,
  inFlightPaise: "0",
};

/**
 * The badge for a tab. Only the kind tabs need translating — the wire calls them
 * `atOnce`/`prebook` where the tab keys match the DB enum (`at_once`/`prebook`).
 */
function countFor(counts: typeof EMPTY_COUNTS, key: Tab): number {
  if (key === "at_once") return counts.atOnce;
  if (key === "prebook") return counts.prebook;
  return counts[key];
}

/** Columns in the table — keeps the skeleton and empty-state `colSpan` honest. */
const COL_COUNT = 8;
const PAGE_SIZE = 25;

/** Why the table is empty — one tab, so one filter to name. */
function emptyMessage(q: string, tab: Tab): string {
  if (q) return "No orders match that search.";
  if (tab === "all") return "No orders yet. They arrive when a buyer places one.";
  // Design 18's copy. No CTA on purpose: the entry point for a booking is the
  // quote flow, and the sentence says so.
  if (tab === "prebook") {
    return "No pre-book orders yet. Bookings arrive from pre-book quotes on an upcoming season.";
  }
  if (tab === "at_once") return "No at-once orders yet.";
  return `No ${ORDER_STATUS_LABEL[tab].toLowerCase()} orders.`;
}

/**
 * Store admin — Orders list (Commerce, M3).
 *
 * The wireframe's Invoiced / Dispatched / Closed tabs are deliberately absent:
 * the deck describes them as echoed back from the ERP invoice chain, which
 * needs the proforma hand-off. The tabs here are the shipped status enum.
 *
 * @see prototype/store/admin/all.html#orders-list · docs/spec/22-store-orders.md
 */
export function OrdersListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);

  // The selected tab is either a status or a kind, never both — see the `Tab`
  // docblock. Filtering happens in the query, never on the page afterwards: the
  // cursor is filter-relative, so client-side filtering would silently shrink
  // pages and hand back a cursor that skips rows.
  const list = trpc.storeOrders.list.useQuery({
    limit: PAGE_SIZE,
    ...(tab === "all" ? {} : isKindTab(tab) ? { kind: tab } : { status: tab }),
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });
  // Tab-independent so the badges and the subtitle don't flicker on tab change.
  const counts = trpc.storeOrders.counts.useQuery();
  const c = counts.data ?? EMPTY_COUNTS;
  const rows = list.data?.items ?? [];

  const resetPaging = () => setCursors([]);

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Orders" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Orders</h1>
          {/* The deck's live stat line, computed rather than written. */}
          <p className="mt-1.5 text-sm text-muted">
            <span className="font-mono tabular-nums text-ink">{c.open}</span> open ·{" "}
            <span className="font-mono tabular-nums text-ink">{orderMoney(c.inFlightPaise)}</span> in
            flight ·{" "}
            <span
              className={
                c.pastDispatch > 0 ? "font-mono tabular-nums text-bad" : "font-mono tabular-nums"
              }
            >
              {c.pastDispatch}
            </span>{" "}
            past dispatch date
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        <TabsRoot
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            {/* `overflow-y-hidden`: the primitive's `overflow-x-auto` also
                computes `overflow-y: auto`, and the count badges make the list
                a pixel taller than its box — enough for a stray scrollbar. */}
            <TabsList aria-label="Filter orders" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} count={countFor(c, t.key)}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search order # / buyer…"
                aria-label="Search orders"
                className="h-8 w-full min-w-60 sm:w-72"
              />
            </div>
          </div>
          {/* Each trigger points `aria-controls` at a panel id; the table below
              isn't a Radix panel, so without these the references dangle. */}
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        <div className="overflow-x-auto">
          {/* 940 → 1010: the Pre-book pill lands in the narrowest column, and
              without the extra width it squeezes the doc number. This moves the
              horizontal-scroll threshold, not any row's content (AC5). */}
          <table className="w-full min-w-[1010px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-4 text-left font-medium">#</th>
                <th className="px-3 py-4 text-left font-medium">Buyer</th>
                <th className="px-3 py-4 text-left font-medium">Date</th>
                <th className="px-3 py-4 text-left font-medium">Dispatch by</th>
                <th className="px-3 py-4 text-right font-medium">Qty</th>
                <th className="px-3 py-4 text-right font-medium">Value</th>
                <th className="px-3 py-4 text-left font-medium">Rep</th>
                <th className="px-3 py-4 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-3" colSpan={COL_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((o) => (
                    <tr
                      key={o.orderId}
                      className="border-b border-line last:border-0 hover:bg-raised"
                    >
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-2">
                          <Link
                            to="/store/admin/orders/$orderId"
                            params={{ orderId: o.orderId }}
                            className="font-mono text-sm font-medium text-ink hover:text-sky-700"
                          >
                            {o.docNumber || "Unnumbered"}
                          </Link>
                          {/* Renders nothing for an at-once row, so those stay
                              byte-identical to before (AC5). */}
                          <PrebookPill kind={o.kind} />
                        </span>
                      </td>
                      <td className="px-3 py-3 text-ink">{o.customerName}</td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(o.createdAt)}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {/* `effectiveDispatchBy`, not `dispatchBy`: a pre-book order
                            promised a delivery window and is judged against the end
                            of it (spec 16 R5). The server derives which one, so this
                            column can't drift from the Past-dispatch pill. */}
                        {shortDay(o.effectiveDispatchBy)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {o.qty}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {orderMoney(o.totalPaise)}
                      </td>
                      <td className="px-3 py-3 text-muted">{o.repName ?? "—"}</td>
                      <td className="px-3 py-3">
                        {/* Overdue overrides the real status in the badge, the
                            way the deck draws it — it's what needs acting on. */}
                        {o.pastDispatch ? (
                          <StatusPill tone="bad" size="sm" dot>
                            Past dispatch
                          </StatusPill>
                        ) : (
                          <StatusPill tone={ORDER_STATUS_TONE[o.status]} size="sm" dot>
                            {ORDER_STATUS_LABEL[o.status]}
                          </StatusPill>
                        )}
                      </td>
                    </tr>
                  ))}
              {!list.isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                    {emptyMessage(q, tab)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "order" : "orders"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((p) => p.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((p) => [...p, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </StoreAdminLayout>
  );
}
